import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ── Theme tokens (matching HiddenVAKReport) ───────────────────────────────────
const T = {
  navy: "#A80D2C",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
};

// ── Static lookup tables ──────────────────────────────────────────────────────
const DIMENSION_LABELS = {
  Listening:    "Listening",
  TPG:          "Talks with Purpose",
  CBA:          "Clarity & Brevity",
  "N-Verbal":   "Non-Verbal Cues",
  "Inf.Share":  "Information Sharing",
  Articulation: "Articulation",
};

const INTERP_TO_BAND = {
  "Excellent":         "good",
  "Very Good":         "good",
  "Suitable":          "sat",
  "Nearly There":      "med",
  "Developing":        "low",
  "Needs improvement": "low",
};

const DIMENSION_DESCRIPTIONS = {
  Listening:
    "Listening measures active attention and comprehension in conversations. A high score here reflects a strong ability to absorb and process spoken information.",
  TPG:
    "Talks with Purpose reflects the ability to communicate with a clear goal in mind and bring intention into every conversation and meeting.",
  CBA:
    "Clarity & Brevity covers the ability to express ideas clearly, briefly, and audibly. A low score here suggests inconsistency in message crispness and delivery.",
  "N-Verbal":
    "Non-Verbal Cues measures awareness and effective use of body language, eye contact, and facial expressions to reinforce communication.",
  "Inf.Share":
    "Information Sharing defines the ability to provide information quickly and accurately to internal and external stakeholders.",
  Articulation:
    "Articulation measures syntax, phrase construction, idiom use, and grammar. A low score here is typically the clearest development need.",
};

const DIMENSION_INTERVENTIONS = {
  Listening: {
    intervention:  "Practice structured active-listening techniques: paraphrase what you hear before responding, and reduce filler responses.",
    expectedShift: "Improved comprehension, fewer misunderstandings, and stronger perceived empathy.",
  },
  TPG: {
    intervention:  "Define a clear objective before every meeting or key conversation. Use a 3-point structure: purpose, message, close.",
    expectedShift: "More focused interactions and a reputation for productive, goal-driven communication.",
  },
  CBA: {
    intervention:  "Adopt a pre-drafting habit before key meetings: opening point, single support point, then close — no more.",
    expectedShift: "Shorter, clearer, and more persuasive communication in both written and verbal settings.",
  },
  "N-Verbal": {
    intervention:  "Record and review yourself presenting; work with a coach on body-language alignment with your spoken words.",
    expectedShift: "Greater perceived confidence and improved listener engagement.",
  },
  "Inf.Share": {
    intervention:  "Build a consistent habit of structured knowledge-sharing — use brief daily or weekly summaries to keep stakeholders informed.",
    expectedShift: "Stronger trust and reduced information bottlenecks across teams.",
  },
  Articulation: {
    intervention:  "Executive coaching on grammar precision, sentence shaping, verbal fluency, and structured oral delivery.",
    expectedShift: "Sharper executive presence and more polished spoken communication.",
  },
};

const DISPLAY_ORDER = ["Inf.Share", "TPG", "Listening", "N-Verbal", "CBA", "Articulation"];

// ── Band → bar fill colour (inline, html2canvas-safe) ────────────────────────
const BAND_FILL = {
  good: "linear-gradient(90deg,#1f8f55,#56c985)",
  sat:  "linear-gradient(90deg,#3f7cac,#74a7d5)",
  med:  "linear-gradient(90deg,#d88a1f,#f0b256)",
  low:  "linear-gradient(90deg,#c2413b,#ea7a71)",
};

// ── Per-dimension fill colours (matching CPQResult visual score dashboard) ───
const DIM_FILL = {
  "Listening":    "linear-gradient(90deg,#1e3a8a,#3b82f6)",
  "TPG":          "linear-gradient(90deg,#7f1d1d,#ef4444)",
  "CBA":          "linear-gradient(90deg,#b45309,#f59e0b)",
  "N-Verbal":     "linear-gradient(90deg,#064e3b,#10b981)",
  "Inf.Share":    "linear-gradient(90deg,#4c1d95,#8b5cf6)",
  "Articulation": "linear-gradient(90deg,#9d174d,#ec4899)",
};

const DIM_BORDER = {
  "Listening":    "#3b82f6",
  "TPG":          "#ef4444",
  "CBA":          "#f59e0b",
  "N-Verbal":     "#10b981",
  "Inf.Share":    "#8b5cf6",
  "Articulation": "#ec4899",
};

const DIM_LABEL_COLOR = {
  "Listening":    "#1e3a8a",
  "TPG":          "#7f1d1d",
  "CBA":          "#b45309",
  "N-Verbal":     "#064e3b",
  "Inf.Share":    "#4c1d95",
  "Articulation": "#9d174d",
};

const BAND_SCALE = [
  { cls: "#5b1f24", label: "Needs improvement", range: "148 & below" },
  { cls: "#8b4b28", label: "Developing",         range: "149–166"    },
  { cls: "#a16f22", label: "Nearly There",        range: "167–184"    },
  { cls: "#375b7a", label: "Suitable",            range: "185–201"    },
  { cls: "#1c6b48", label: "Very Good",           range: "202–217"    },
  { cls: "#0f8b59", label: "Excellent",           range: "218+"       },
];

const PILL_STYLE = {
  good: { background: "#f0fdf4", color: "#1f8f55", border: "1px solid #bbf7d0" },
  sat:  { background: "#dbeafe", color: "#3f7cac", border: "1px solid #bfdbfe" },
  med:  { background: "#fffbeb", color: "#d88a1f", border: "1px solid #fde68a" },
  low:  { background: "#fee2e2", color: "#c2413b", border: "1px solid #fecaca" },
};

/**
 * HiddenCPQReport
 * ───────────────
 * Renders the CPQ report offscreen, captures each .cpq-page with html2canvas,
 * stitches into a multi-page A4 PDF via jsPDF, then saves it.
 *
 * Design matches HiddenVAKReport:
 *   • Red gradient cover with SVG logo panel (Acumen_25_Years_LOGO)
 *   • Red gradient back cover with gold radial highlight
 *   • Satoshi font, same card/bar/callout/list system
 *   • Text justify throughout
 *   • hasRun guard (works for both permanent and remount parent patterns)
 *
 * Props:
 *   participantData  – null | { userInfo: { name, email, phone }, scores: { ...dimensions, Grand_Total }, submission_time }
 *   onComplete       – () => void
 */
export default function HiddenCPQReport({ participantData, onComplete }) {
  const containerRef = useRef(null);
  const hasRun = useRef(false);

  // ── Load html2canvas + jsPDF once ────────────────────────────────────────
  useEffect(() => {
    const load = (src, id) => {
      if (document.getElementById(id)) return;
      const s = document.createElement("script");
      s.id = id; s.src = src;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-cpq-v2");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",        "jspdf-cpq-v2");
  }, []);

  // ── Generate PDF whenever participantData is set ──────────────────────────
  useEffect(() => {
    if (!participantData) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const generate = async () => {
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      await new Promise((r) => setTimeout(r, 1000));

      const el = containerRef.current;
      if (!el) { hasRun.current = false; onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".cpq-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData  = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width  / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX   = 25.4 / 96;
          const naturalW    = logicalW * MM_PER_PX;
          const naturalH    = logicalH * MM_PER_PX;
          const scaleToW    = A4_W / naturalW;
          const projectedH  = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2;  yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`CPQ_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("CPQ PDF generation failed", err);
      } finally {
        hasRun.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]);

  if (!participantData) return null;

  // ── Derive all display values ─────────────────────────────────────────────
  const { userInfo = {}, scores = {}, submission_time } = participantData;

  const grandTotal     = scores["Grand_Total"] || { score: 0, out_of: 240, interpretation: "—" };
  const totalScore     = grandTotal.score;
  const maximumScore   = grandTotal.out_of;
  const interpretation = grandTotal.interpretation;

  const participant    = userInfo.name || "—";
  const overallResult  = `${totalScore} / ${maximumScore}`;

  const dateStr = submission_time
    ? new Date(submission_time).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const bandClass = (interp) => INTERP_TO_BAND[interp] ?? "sat";

  const scorePct           = maximumScore > 0 ? (totalScore / maximumScore) * 100 : 0;
  const indicatorFillWidth = `${scorePct.toFixed(1)}%`;
  const benchmarkPct       = (218 / maximumScore) * 100;
  const markerLeft         = `${benchmarkPct.toFixed(1)}%`;

  const dimensions = DISPLAY_ORDER
    .filter((key) => scores[key])
    .map((key) => ({
      key,
      label:     DIMENSION_LABELS[key] || key,
      score:     scores[key].score,
      max:       scores[key].out_of,
      band:      bandClass(scores[key].interpretation),
      bandLabel: scores[key].interpretation,
    }));

  const strongestAreas = [...dimensions]
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((d) => ({ title: d.label, body: DIMENSION_DESCRIPTIONS[d.key] || "" }));

  const priorityGaps = [...dimensions]
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map((d) => ({ title: d.label, body: DIMENSION_DESCRIPTIONS[d.key] || "" }));

  const priorityActions = [...dimensions]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map((d) => ({
      focusArea:     d.label,
      currentScore:  `${d.score} / ${d.max}`,
      intervention:  DIMENSION_INTERVENTIONS[d.key]?.intervention  || "—",
      expectedShift: DIMENSION_INTERVENTIONS[d.key]?.expectedShift || "—",
    }));

  const topDim    = strongestAreas[0]?.title || "—";
  const bottomDim = priorityGaps[0]?.title   || "—";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

      <style>{`
        /* ── Root ── */
        .cpq-pdf-root {
          font-family: 'Satoshi', sans-serif;
          font-size: 16px;
          background: #F5F8FC;
          color: #1D2733;
          line-height: 1.55;
        }
        .cpq-pdf-root .report { max-width:1100px; margin:0 auto; padding:1.5rem 1rem 3rem; }

        /* ── Page shell ── */
        .cpq-pdf-root .cpq-page {
          background:#ffffff; border:1px solid #d9e0e7; border-radius:28px;
          box-shadow:0 18px 40px rgba(14,39,68,.10); overflow:hidden; margin-bottom:2rem;
        }

        /* ── Cover ── */
        .cpq-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .cpq-pdf-root .cover-inner {
          padding:3.5rem; display:flex; flex-direction:column;
          justify-content:space-between; min-height:1556px;
        }
        .cpq-pdf-root .brand {
          display:flex; align-items:center; justify-content:space-between;
          gap:1rem; position:relative; z-index:2;
        }
        .cpq-pdf-root .badge {
          border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1);
          padding:.5rem 1rem; border-radius:999px; font-size:0.76rem;
        }
        /* Logo panel — identical SVG shape to HiddenVAKReport */
        .cpq-pdf-root .logo-panel {
          position:absolute; top:0; right:0;
          width:500px; height:250px;
          display:flex; align-items:center; justify-content:center; z-index:1;
        }
        .cpq-pdf-root .logo-panel-inner {
          display:flex; flex-direction:column; align-items:center;
          position:relative; z-index:2; padding-left:60px; padding-bottom:25px;
        }
        .cpq-pdf-root .logo-img { height:65px; width:auto; object-fit:contain; flex-shrink:0; }

        .cpq-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .cpq-pdf-root .cover-title p {
          font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; text-align:justify;
        }
        .cpq-pdf-root .cover-grid {
          display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem;
        }
        .cpq-pdf-root .meta {
          background:rgba(255,255,255,.1); font-size:1.2rem;
          border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem;
        }
        .cpq-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .cpq-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .cpq-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
        .cpq-pdf-root .cover-badge  {
          background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18);
          padding:.5rem .9rem; border-radius:999px; font-size:0.95rem;
        }
        .cpq-pdf-root .footer-note {
          display:flex; justify-content:space-between; gap:1rem;
          color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:1rem;
          position:relative; z-index:2;
        }

        /* ── Inner pages ── */
        .cpq-pdf-root .page-inner { padding:2.5rem; }
        .cpq-pdf-root .topbar {
          display:flex; justify-content:space-between; align-items:flex-start;
          gap:1rem; margin-bottom:2rem;
        }
        .cpq-pdf-root .section-kicker {
          color:#c62828; font-size:0.8rem; text-transform:uppercase;
          letter-spacing:.12em; font-weight:700;
        }
        .cpq-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }

        /* ── Cards ── */
        .cpq-pdf-root .card {
          background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%);
          border:1px solid #d9e0e7; border-radius:20px; padding:1.5rem;
        }
        .cpq-pdf-root .card h3 { margin:0 0 .75rem; font-size:1.2rem; color:#0f2744; }
        .cpq-pdf-root .lead { color:#334155; font-size:1rem; margin:0; text-align:justify; }

        /* ── Pill ── */
        .cpq-pdf-root .pill {
          display:inline-flex; align-items:center; gap:.45rem;
          padding:.5rem .85rem; border-radius:999px; font-size:0.8rem;
          font-weight:700; text-transform:uppercase; letter-spacing:.06em;
        }

        /* ── Summary grid (page 2: left wider, right score panel) ── */
        .cpq-pdf-root .summary-grid { display:grid; grid-template-columns:1.15fr .85fr; gap:1.5rem; }

        /* ── Mini stat boxes ── */
        .cpq-pdf-root .mini-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.5rem; }
        .cpq-pdf-root .mini { background:#eef2f6; border-radius:16px; padding:1rem; }
        .cpq-pdf-root .mini span   { display:block; font-size:0.75rem; color:#6b7280; margin-bottom:.25rem; }
        .cpq-pdf-root .mini strong { font-size:1.4rem; color:#0f2744; }

        /* ── Score ring card (dark navy panel) ── */
        .cpq-pdf-root .visual-card { background:#0f2744; color:white; border-radius:24px; padding:1.5rem; }
        .cpq-pdf-root .visual-card h3 { color:white; margin:0 0 1rem; font-size:1.2rem; }
        .cpq-pdf-root .score-wrap { display:grid; place-items:center; gap:.75rem; }
        .cpq-pdf-root .score-ring { width:200px; height:200px; display:grid; place-items:center; position:relative; }
        .cpq-pdf-root .score-core { position:relative; text-align:center; z-index:2; }
        .cpq-pdf-root .score-core strong { display:block; font-size:2.6rem; color:#0f2744; line-height:1; }
        .cpq-pdf-root .score-core span   { display:block; color:#6b7280; font-size:0.85rem; }

        /* ── Indicator bar ── */
        .cpq-pdf-root .indicator {
          margin-top:1rem; background:rgba(255,255,255,.07);
          border-radius:16px; padding:1rem; border:1px solid rgba(255,255,255,.08);
        }
        .cpq-pdf-root .indicator-track {
          height:16px; background:rgba(255,255,255,.12);
          border-radius:999px; overflow:hidden; position:relative;
        }
        .cpq-pdf-root .indicator-fill {
          height:100%; background:linear-gradient(90deg,#6fb2ff 0%,#b9d8ff 100%); border-radius:999px;
        }
        .cpq-pdf-root .ind-row {
          display:flex; justify-content:space-between;
          font-size:0.75rem; color:rgba(255,255,255,.72); margin-top:.6rem;
        }

        /* ── Band scale ── */
        .cpq-pdf-root .band-scale {
          display:grid; grid-template-columns:repeat(6,1fr); gap:.5rem; margin:1rem 0;
        }
        .cpq-pdf-root .band {
          border-radius:14px; padding:.7rem .5rem; min-height:80px;
          display:flex; flex-direction:column; justify-content:space-between; font-size:0.72rem;
        }
        .cpq-pdf-root .band strong { font-size:0.8rem; color:white; }
        .cpq-pdf-root .band em     { font-style:normal; color:rgba(255,255,255,.8); }

        /* ── Bars (Page 3) ── */
        .cpq-pdf-root .bars    { display:grid; gap:0.5rem; }
        .cpq-pdf-root .bar-row { display:grid; grid-template-columns:180px 1fr 90px; gap:1rem; align-items:center; padding:0.4rem 0.75rem; border-radius:10px; border-left:3px solid transparent; }
        .cpq-pdf-root .bar-label { font-size:0.9rem; font-weight:700; color:#0f2744; }
        .cpq-pdf-root .track { height:16px; background:#e9eef3; border-radius:999px; overflow:hidden; }
        .cpq-pdf-root .fill  { height:100%; border-radius:999px; }
        .cpq-pdf-root .score-box { text-align:right; font-variant-numeric:tabular-nums; }
        .cpq-pdf-root .score-box strong { display:block; color:#0f2744; font-size:0.9rem; }
        .cpq-pdf-root .score-box span   { font-size:0.75rem; font-weight:700; }

        /* ── Two-col ── */
        .cpq-pdf-root .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1.5rem; }

        /* ── List / items ── */
        .cpq-pdf-root .list { display:grid; gap:.9rem; }
        .cpq-pdf-root .item {
          border:1px solid #d9e0e7; border-radius:16px; padding:1rem; background:#fff;
        }
        .cpq-pdf-root .item strong { display:block; color:#0f2744; margin-bottom:.25rem; }
        .cpq-pdf-root .item p { margin:0; color:#475569; font-size:0.88rem; text-align:justify; }

        /* ── Table ── */
        .cpq-pdf-root table { width:100%; border-collapse:collapse; }
        .cpq-pdf-root th, .cpq-pdf-root td {
          padding:.85rem .9rem; border-bottom:1px solid #d9e0e7;
          text-align:left; vertical-align:top; font-size:0.88rem;
        }
        .cpq-pdf-root th {
          background:#eff4f8; color:#0f2744;
          font-size:0.72rem; text-transform:uppercase; letter-spacing:.08em;
        }
        .cpq-pdf-root tr:last-child td { border-bottom:none; }
        .cpq-pdf-root td { text-align:justify; }

        /* ── Callout ── */
        .cpq-pdf-root .callout {
          background:linear-gradient(135deg,#fff7f7,#fbeaea);
          border:1px solid #d9e0e7; border-radius:18px;
          padding:1.25rem 1.5rem; color:#334155; text-align:justify;
        }

        /* ── Back cover ── */
        .cpq-pdf-root .back-cover { min-height:1556px; color:white; }
        .cpq-pdf-root .back-cover .page-inner {
          min-height:1556px; display:flex; flex-direction:column; justify-content:space-between;
        }
        .cpq-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .cpq-pdf-root .closing p  { text-align:justify; max-width:100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
        .cpq-pdf-root .contact-grid {
          display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem;
        }
        .cpq-pdf-root .contact {
          background:rgba(193,24,24,.1); border:1px solid rgba(255,255,255,.16);
          border-radius:16px; padding:.9rem;
        }
        .cpq-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .cpq-pdf-root .contact strong { font-size:0.92rem; }
        .cpq-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .cpq-pdf-root .light { color:rgba(255,255,255,.66); }
      `}</style>

      <div className="cpq-pdf-root">
        <main className="report">

          {/* ══════════════════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════════════════ */}
          <section className="cpq-page cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
                minHeight: "1556px",
                flexDirection: "column",
              }}
            >
              {/* SVG logo panel — identical shape to HiddenVAKReport */}
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Communication Proficiency <br></br>Assessment</h2>
                    <p>
                      This comprehensive assessment report presents an analysis of your communication
                      proficiency across six key dimensions, helping identify your strongest areas and
                      the targeted development actions that will have the greatest impact on your
                      professional effectiveness.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta"><span>Participant</span><strong>{participant}</strong></div>
                    <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                    <div className="meta"><span>Overall Result</span><strong>{overallResult}</strong></div>
                  </div>

                  <div className="cover-badges">
                    {["Executive Summary", "Six Communication Dimensions", "Development Plan"].map((b) => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note">
                  <div>Based on the six communication dimensions defined in the Communication Proficiency Questionnaire.</div>
                  {/* <div>Excellent starts at 218+.</div> */}
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 2 — EXECUTIVE SUMMARY
          ══════════════════════════════════════════════════════ */}
          <section className="cpq-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Summary</h2>
                </div>
                <div
                  className="pill"
                  style={PILL_STYLE[bandClass(interpretation)] || PILL_STYLE.sat}
                >
                  Overall Band: {interpretation}
                </div>
              </div>

              <div className="summary-grid">
                {/* Left — purpose + mini stats + callout */}
                <div className="card">
                  <h3>Assessment Purpose</h3>
                  <p className="lead">
                    This report is designed to identify clear communication strengths and priority
                    development areas after an individual completes the Communication Proficiency
                    Questionnaire. The score interpretation follows the defined total score bands:
                    Needs improvement (148 and below), Developing (149–166), Nearly There (167–184),
                    Suitable (185–201), Very Good (202–217), and Excellent (218+).
                  </p>

                  <div className="mini-grid">
                    <div className="mini">
                      <span>Total score</span>
                      <strong>{totalScore}</strong>
                    </div>
                    <div className="mini">
                      <span>Maximum score</span>
                      <strong>{maximumScore}</strong>
                    </div>
                    <div className="mini">
                      <span>Interpretation</span>
                      <strong>{interpretation}</strong>
                    </div>
                  </div>

                  <div className="callout" style={{ marginTop: "1.5rem" }}>
                    <strong>{participant}</strong> presents a communication profile with a standout
                    strength in <strong>{topDim}</strong> and a priority development area
                    in <strong>{bottomDim}</strong>. The overall score
                    of <strong>{totalScore}</strong> places this participant in
                    the <strong>{interpretation}</strong> band, pointing to clear, actionable
                    areas for growth.
                  </div>
                </div>

                {/* Right — score ring (dark panel) */}
                <div className="visual-card">
                  <h3>Overall Score</h3>
                  <div className="score-wrap">
                    <div className="score-ring">
                      <svg
                        viewBox="0 0 200 200"
                        width="200"
                        height="200"
                        style={{ position: "absolute", top: 0, left: 0 }}
                      >
                        <circle cx="100" cy="100" r="80" fill="none" stroke="#dfe7ee" strokeWidth="20" />
                        <circle cx="100" cy="100" r="70" fill="white" />
                        <circle
                          cx="100" cy="100" r="80"
                          fill="none"
                          stroke="#c62828"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${(2 * Math.PI * 80).toFixed(3)}`}
                          strokeDashoffset={`${(2 * Math.PI * 80 * (1 - scorePct / 100)).toFixed(3)}`}
                          transform="rotate(-90 100 100)"
                        />
                      </svg>
                      <div className="score-core">
                        <strong>{totalScore}</strong>
                        <span>out of {maximumScore}</span>
                      </div>
                    </div>
                    <div className="pill" style={PILL_STYLE[bandClass(interpretation)] || PILL_STYLE.sat}>
                      {interpretation}
                    </div>
                  </div>

                  <div className="indicator">
                    <div className="indicator-track" style={{ position: "relative" }}>
                      <div className="indicator-fill" style={{ width: indicatorFillWidth }} />
                      <div style={{
                        position: "absolute", top: "-5px", left: markerLeft,
                        width: "3px", height: "26px",
                        background: "#d6a23f", borderRadius: "99px",
                      }} />
                    </div>
                    <div className="ind-row">
                      <span>Current score</span>
                      <span>Benchmark for Excellent</span>
                    </div>
                    <div className="ind-row">
                      <strong style={{ color: "white" }}>{totalScore}</strong>
                      <strong style={{ color: "#d6a23f" }}>218+</strong>
                    </div>
                  </div>

                  <div className="band-scale">
                    {BAND_SCALE.map((b) => (
                      <div key={b.label} className="band" style={{ background: b.cls }}>
                        <strong>{b.label}</strong>
                        <em>{b.range}</em>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 3 — DIMENSION PROFILE
          ══════════════════════════════════════════════════════ */}
          <section className="cpq-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Dimension Profile</h2>
                </div>
                <div className="small">Max 40 per dimension</div>
              </div>

              <div className="card" style={{ marginBottom: "1.5rem" }}>
                <h3>Visual Score Dashboard</h3>
                <div className="bars">
                  {dimensions.map((dim) => (
                    <div
                      className="bar-row"
                      key={dim.key}
                      style={{ borderLeftColor: DIM_BORDER[dim.key] || "#3f7cac" }}
                    >
                      <div
                        className="bar-label"
                        style={{ color: DIM_LABEL_COLOR[dim.key] || "#0f2744" }}
                      >
                        {dim.label}
                      </div>
                      <div className="track">
                        <div
                          className="fill"
                          style={{
                            width: `${(dim.score / dim.max) * 100}%`,
                            background: DIM_FILL[dim.key] || BAND_FILL[dim.band] || BAND_FILL.sat,
                          }}
                        />
                      </div>
                      <div className="score-box">
                        <strong>{dim.score} / {dim.max}</strong>
                        <span style={{ color: DIM_LABEL_COLOR[dim.key] || "#6b7280" }}>
                          {dim.bandLabel}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="two-col">
                <div className="card">
                  <h3>Strongest Areas</h3>
                  <div className="list">
                    {strongestAreas.map((area) => (
                      <div className="item" key={area.title}>
                        <strong>{area.title}</strong>
                        <p>{area.body}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3>Priority Gaps</h3>
                  <div className="list">
                    {priorityGaps.map((gap) => (
                      <div className="item" key={gap.title}>
                        <strong>{gap.title}</strong>
                        <p>{gap.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 4 — DEVELOPMENT PLAN
          ══════════════════════════════════════════════════════ */}
          <section className="cpq-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Development Plan</h2>
                </div>
                <div className="small">90-day focus areas</div>
              </div>

              <div className="card" style={{ marginBottom: "1.5rem" }}>
                <h3>Priority Actions</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Focus area</th>
                      <th>Current score</th>
                      <th>Recommended intervention</th>
                      <th>Expected shift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priorityActions.map((row) => (
                      <tr key={row.focusArea}>
                        <td>{row.focusArea}</td>
                        <td>{row.currentScore}</td>
                        <td>{row.intervention}</td>
                        <td>{row.expectedShift}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="two-col">
                <div className="card">
                  <h3>How to Read This Report</h3>
                  <p className="lead">
                    This report follows a clear four-page sequence: a cover page, a concise
                    executive summary with score interpretation, a visual dimension profile,
                    and action-oriented development pages. It is designed for individual use
                    while keeping content practical and visually easy to present.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    The objective of this assessment is to identify which area needs to be
                    worked on and to focus the participant on improvement rather than just
                    where they stand today. This report is most useful when reviewed alongside
                    a manager or facilitator in a guided debrief.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 5 — BACK COVER
          ══════════════════════════════════════════════════════ */}
          <section className="cpq-page back-cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
                minHeight: "1556px",
              }}
            >
              {/* Header bar */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Moving Forward with Intentional Communication</h2>
                    <p>
                      Communication proficiency is not a fixed trait. It is a capability that
                      strengthens through awareness, disciplined practice, and consistent feedback.
                      Acumen Business Catalyst uses reports like this to turn assessment data into
                      focused development conversations — helping individuals close their gaps and
                      build a more impactful professional presence.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Communication Design</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      For this participant, the most effective development path starts with the
                      dimension scoring lowest on the profile — <strong style={{ color: "white" }}>{bottomDim}</strong> —
                      and applies the targeted intervention consistently over a 90-day period.
                      Pairing that focus with regular debrief conversations and peer feedback will
                      accelerate observable improvement and build lasting communication habits.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>CPQ Assessment Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Communication Proficiency Questionnaire scoring framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}